/**
 * AURA Configuration Parser
 * Reads configuration from data attributes or global object
 */

import { AuraConfig } from './types';
import { Logger } from './logger';

const logger = new Logger('Config');

const DEFAULT_CONFIG: Partial<AuraConfig> = {
  position: 'bottom-right',
  theme: 'auto',
  language: 'en',
  zIndex: 999999,
  enabledFeatures: {
    profiles: true,
    contentAdjustments: true,
    colorAdjustments: true,
    orientationAdjustments: true,
    aiRemediation: true,
    analytics: true,
    statement: true,
  },
};

/**
 * Parse configuration from various sources
 */
export function parseConfig(): AuraConfig {
  // Try to get config from global object first
  const globalConfig = getGlobalConfig();

  // Then from script data attributes
  const scriptConfig = getScriptDataAttributes();

  // Merge with defaults
  const merged = mergeConfigs(DEFAULT_CONFIG, scriptConfig, globalConfig);

  // Validate required fields
  if (!merged.appId) {
    logger.warn('No appId configured, using "default"');
    merged.appId = 'default';
  }

  logger.info('Configuration loaded', {
    appId: merged.appId,
    position: merged.position,
    theme: merged.theme,
  });

  return merged as AuraConfig;
}

/**
 * Get configuration from window.Aura.config
 */
function getGlobalConfig(): Partial<AuraConfig> {
  const global = (window as unknown as Window & { Aura?: { config?: AuraConfig } }).Aura;
  return global?.config ?? {};
}

/**
 * Get configuration from script tag data attributes
 */
function getScriptDataAttributes(): Partial<AuraConfig> {
  const script = document.querySelector(
    'script[src*="widget.iife.js"][data-app-id], script[data-aura]'
  ) as HTMLScriptElement | null;

  if (!script) return {};

  const config: Partial<AuraConfig> = {};

  // Parse data attributes
  const appId = script.dataset.appId;
  if (appId) config.appId = appId;

  const position = script.dataset.position as AuraConfig['position'];
  if (position) config.position = position;

  const theme = script.dataset.theme as AuraConfig['theme'];
  if (theme) config.theme = theme;

  const language = script.dataset.language;
  if (language) config.language = language;

  const zIndex = script.dataset.zIndex;
  if (zIndex) config.zIndex = parseInt(zIndex, 10);

  const remediationApiUrl = script.dataset.remediationApiUrl;
  if (remediationApiUrl) config.remediationApiUrl = remediationApiUrl;

  const preferenceSyncApiUrl = script.dataset.preferenceSyncApiUrl;
  if (preferenceSyncApiUrl) config.preferenceSyncApiUrl = preferenceSyncApiUrl;

  const analyticsApiUrl = script.dataset.analyticsApiUrl;
  if (analyticsApiUrl) config.analyticsApiUrl = analyticsApiUrl;

  const logApiUrl = script.dataset.logApiUrl;
  if (logApiUrl) config.logApiUrl = logApiUrl;

  // Auto-detect API base URL from the widget script's own origin when
  // explicit API URLs aren't provided. The widget and API are served from
  // the same IIS site (e.g. https://aura.dev.nbsintranet.com), so the API
  // is always at {origin}/api/v1.
  if (!config.remediationApiUrl || !config.analyticsApiUrl || !config.logApiUrl) {
    const autoBase = detectApiBaseUrl(script);
    if (autoBase) {
      if (!config.remediationApiUrl) config.remediationApiUrl = autoBase;
      if (!config.preferenceSyncApiUrl) config.preferenceSyncApiUrl = autoBase;
      if (!config.analyticsApiUrl) config.analyticsApiUrl = autoBase;
      if (!config.logApiUrl) config.logApiUrl = `${autoBase}/logs`;
    }
  }

  return config;
}

/**
 * Derive the API base URL from the widget script's own src attribute.
 * If the script is at https://aura.dev.nbsintranet.com/v1/widget.iife.js,
 * the API base is https://aura.dev.nbsintranet.com/api/v1.
 */
function detectApiBaseUrl(script: HTMLScriptElement): string | null {
  try {
    const src = script.src;
    if (!src) return null;
    const url = new URL(src);
    return `${url.origin}/api/v1`;
  } catch (_) {
    logger.warn('Could not auto-detect API URL from script src');
    return null;
  }
}

/**
 * Deep merge configuration objects
 */
function mergeConfigs(...configs: Partial<AuraConfig>[]): Partial<AuraConfig> {
  const result: Record<string, unknown> = {};

  for (const config of configs) {
    for (const [key, value] of Object.entries(config)) {
      if (value === undefined) continue;

      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        typeof result[key] === 'object'
      ) {
        result[key] = { ...(result[key] as object), ...value };
      } else {
        result[key] = value;
      }
    }
  }

  return result as Partial<AuraConfig>;
}

/**
 * Get the current configuration (must be called after init)
 */
let currentConfig: AuraConfig | null = null;

export function getConfig(): AuraConfig {
  if (!currentConfig) {
    currentConfig = parseConfig();
  }
  return currentConfig;
}

/**
 * Update configuration at runtime
 */
export function updateConfig(partial: Partial<AuraConfig>): AuraConfig {
  currentConfig = { ...getConfig(), ...partial };
  return currentConfig;
}
